/**
 * Web Push 订阅（客户端侧）。
 *
 * 两个静默杀手：
 *
 * 1. **VAPID 密钥换了，浏览器里的旧订阅还在**。浏览器不会告诉你订阅已经对不上号了，
 *    它照样存在、照样返回给你，只是服务端再也推不动。对策是每次订阅前把已存订阅的
 *    applicationServerKey 和服务器当前公钥比一遍，不一致就退订重订（订阅自愈）。
 *    （服务端侧的对应措施是把 VAPID 密钥持久化到数据目录，见 @pwaship/push-server。）
 *
 * 2. **订阅端点会失效**（用户清站点数据、推送服务过期）。服务端发现 404/410 要主动删，
 *    客户端这边则要在每次启动时把当前订阅重新上报一次，让服务端的记录跟得上。
 */
import { explainPushSupport } from './capability.js';
import { fail, failFrom, ok } from './result.js';
export function base64UrlToUint8Array(base64Url) {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = `${base64Url}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1)
        out[i] = raw.charCodeAt(i);
    return out;
}
export function uint8ArrayToBase64Url(bytes) {
    const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let binary = '';
    for (const byte of arr)
        binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/** 已存订阅用的公钥是否还是服务器现在这把。不一致 = 这个订阅已经是死的了。 */
export function subscriptionMatchesKey(subscription, vapidPublicKey) {
    const existing = subscription.options?.applicationServerKey;
    if (!existing)
        return false;
    try {
        return uint8ArrayToBase64Url(existing) === vapidPublicKey.replace(/=+$/, '');
    }
    catch {
        return false;
    }
}
/**
 * 订阅推送。已有订阅且公钥一致时直接复用（并重新上报一次）。
 *
 * 前置条件由调用方保证：通知权限已 granted。这里不代为申请——申请必须在用户手势内，
 * 而这个函数天然是异步链的末端。
 */
export async function subscribePush(options) {
    const support = explainPushSupport();
    if (!support.ok)
        return support;
    if (!options.vapidPublicKey)
        return fail('failed', '缺少 VAPID 公钥');
    const { registration, vapidPublicKey } = options;
    try {
        let subscription = await registration.pushManager.getSubscription();
        if (subscription && !subscriptionMatchesKey(subscription, vapidPublicKey)) {
            // 订阅自愈：服务器换过 VAPID 密钥，这个订阅已经推不动了，且不会有任何报错
            await subscription.unsubscribe().catch(() => undefined);
            subscription = null;
        }
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64UrlToUint8Array(vapidPublicKey),
            });
        }
        const json = subscription.toJSON();
        // 每次启动都重报一次：服务端的记录才跟得上端点变化
        if (options.report)
            await options.report(json);
        return ok(json);
    }
    catch (e) {
        // 权限被拒时 subscribe() 抛 NotAllowedError
        const name = e instanceof Error ? e.name : '';
        if (name === 'NotAllowedError')
            return fail('permission-denied');
        return failFrom('failed', e);
    }
}
export async function unsubscribePush(registration, revoke) {
    try {
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription)
            return ok();
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        // 先本地退订再通知服务端：反过来的话服务端删了、本地还留着，下次启动会重新上报一个死订阅
        if (revoke)
            await revoke(endpoint);
        return ok();
    }
    catch (e) {
        return failFrom('failed', e);
    }
}
export async function currentSubscription(registration) {
    try {
        const sub = await registration.pushManager.getSubscription();
        return sub ? sub.toJSON() : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=push-client.js.map